import { useEffect, useRef, useState } from 'react'
import { Socket } from 'socket.io-client'
import { createWebchatSocket } from '@/services/webchatSocket'
import { getApiBaseUrl } from '@/config/env'
import toast from 'react-hot-toast'
import {
  RotateCcw,
  Bot,
  CheckCircle2,
  Image as ImageIcon,
  X,
  Maximize2,
} from 'lucide-react'
import type { Product } from '@/lib/api/products'
import {
  getTestChatSession,
  sendTestChatMessage,
  resetTestChatSession,
} from '@/lib/api/botSettings'

interface ChatMessage {
  id?: string
  role: 'bot' | 'user'
  text: string
  time: string
  stage?: string
  mediaUrl?: string
  mediaMimetype?: string
}

interface WhatsAppPreviewProps {
  botName: string
  avatar?: string | null
  welcomeMessage?: string | null
  typingSimulation: boolean
  previewProduct?: Product | null
  previewFAQ?: { question: string; answer: string } | null
  interactive?: boolean
}

function getTime() {
  return new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })
}

function getOrCreateVisitorId(): string {
  const key = 'saas_bot_test_visitor_id'
  let id = sessionStorage.getItem(key)
  if (!id) {
    id = 'preview_vis_' + Date.now() + '_' + Math.random().toString(36).slice(2, 7)
    sessionStorage.setItem(key, id)
  }
  return id
}

function getStageBadge(stage?: string) {
  if (!stage) return { label: 'Descubrimiento', icon: '🔍', color: 'bg-blue-100 text-blue-800 border-blue-200' }
  const s = stage.toUpperCase()
  if (s.includes('CLOS')) return { label: 'Cierre de Venta', icon: '🤝', color: 'bg-emerald-100 text-emerald-800 border-emerald-300 font-semibold' }
  if (s.includes('CHECK')) return { label: 'Checkout / Pago', icon: '💳', color: 'bg-purple-100 text-purple-800 border-purple-200 font-semibold' }
  if (s.includes('OBJEC')) return { label: 'Objeción', icon: '💬', color: 'bg-amber-100 text-amber-800 border-amber-200' }
  if (s.includes('RECOM')) return { label: 'Recomendación', icon: '🛍️', color: 'bg-indigo-100 text-indigo-800 border-indigo-200' }
  if (s.includes('QUAL')) return { label: 'Calificación', icon: '🎯', color: 'bg-cyan-100 text-cyan-800 border-cyan-200' }
  return { label: 'Descubrimiento', icon: '🔍', color: 'bg-blue-100 text-blue-800 border-blue-200' }
}

function getBotResponse(message: string, botName: string): string {
  const lower = message.toLowerCase()
  if (/precio|costo|cu[aá]nto|vale/.test(lower))
    return 'Nuestros precios son muy competitivos 💰 ¿Qué producto te interesa?'
  if (/env[ií]o|delivery|despacho|entrega/.test(lower))
    return 'Hacemos envíos a todo el país en 2-5 días hábiles 📦'
  if (/horario|hora|atenci[oó]n|disponible/.test(lower))
    return 'Atendemos Lun-Vie de 9am a 6pm. ¡También puedes escribirnos fuera de horario! 🕐'
  if (/producto|cat[aá]logo|tienes|tienen/.test(lower))
    return 'Tenemos muchas opciones disponibles. ¿Hay alguna categoría que te interese? 🛍️'
  if (/hola|buenas|buenos|saludos/.test(lower))
    return `¡Hola! Soy ${botName}. ¿En qué puedo ayudarte hoy? 😊`
  if (/gracias|thank/.test(lower))
    return '¡De nada! ¿Hay algo más en lo que pueda ayudarte? 😊'
  if (/pago|pagar|transferencia|tarjeta/.test(lower))
    return 'Aceptamos efectivo, tarjetas de crédito/débito y transferencias bancarias 💳'
  return 'Gracias por tu mensaje. Un momento mientras proceso tu consulta... 🤖'
}

export default function WhatsAppPreview({
  botName,
  avatar,
  welcomeMessage,
  typingSimulation,
  previewProduct,
  previewFAQ,
  interactive = true,
}: WhatsAppPreviewProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [botTyping, setBotTyping] = useState(false)
  const [userInput, setUserInput] = useState('')
  const [mode, setMode] = useState<'real' | 'static'>('real')
  const [socketStatus, setSocketStatus] = useState<'connecting' | 'connected' | 'offline'>('connecting')
  const [currentStage, setCurrentStage] = useState<string>('DISCOVERY')
  const [selectedProduct, setSelectedProduct] = useState<{ id?: string; name?: string; price?: number } | null>(null)
  const [isResetting, setIsResetting] = useState(false)
  const [pendingImage, setPendingImage] = useState<{ dataUrl: string; name: string; file: File } | null>(null)
  const [zoomedImage, setZoomedImage] = useState<string | null>(null)

  const chatEndRef = useRef<HTMLDivElement>(null)
  const socketRef = useRef<Socket | null>(null)
  const visitorIdRef = useRef<string>(getOrCreateVisitorId())
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Init welcome message
  useEffect(() => {
    if (!welcomeMessage) {
      setMessages([])
      return
    }
    if (messages.length === 0) {
      if (typingSimulation) {
        setBotTyping(true)
        const t = setTimeout(() => {
          setBotTyping(false)
          setMessages([{ role: 'bot', text: welcomeMessage, time: getTime() }])
        }, 1200)
        return () => clearTimeout(t)
      } else {
        setMessages([{ role: 'bot', text: welcomeMessage, time: getTime() }])
      }
    }
  }, [welcomeMessage, typingSimulation])

  // Setup WebChat Socket.IO connection in real mode
  useEffect(() => {
    if (mode !== 'real') {
      if (socketRef.current) {
        socketRef.current.disconnect()
        socketRef.current = null
      }
      setSocketStatus('offline')
      return
    }

    let isMounted = true
    const visitorId = visitorIdRef.current

    setSocketStatus('connecting')

    getTestChatSession(visitorId)
      .then((session) => {
        if (!isMounted) return

        const socketUrl = getApiBaseUrl()
        const socket = createWebchatSocket(socketUrl, {
          widgetToken: session.widgetToken,
          tenantSlug: session.tenantSlug,
          visitorId: session.visitorId,
        })

        socket.on('connect', () => {
          if (isMounted) setSocketStatus('connected')
        })

        socket.on('disconnect', () => {
          if (isMounted) setSocketStatus('offline')
        })

        socket.on('connect_error', () => {
          if (isMounted) setSocketStatus('offline')
        })

        let typingTimeout: any = null

        socket.on('webchat:message', (payload: any) => {
          if (!isMounted) return
          if (typingTimeout) clearTimeout(typingTimeout)
          if (payload?.from === 'agent' && (payload?.text || payload?.mediaUrl)) {
            setBotTyping(false)
            setMessages((prev) => {
              const last = prev[prev.length - 1]
              if (
                last &&
                last.role === 'bot' &&
                last.text === payload.text &&
                last.mediaUrl === payload.mediaUrl
              ) {
                return prev
              }
              return [
                ...prev,
                {
                  id: payload.id,
                  role: 'bot',
                  text: payload.text || '',
                  mediaUrl: payload.mediaUrl,
                  mediaMimetype: payload.mimetype,
                  time: getTime(),
                },
              ]
            })
          }
        })

        socket.on('webchat:typing', (payload: any) => {
          if (!isMounted) return
          const isTyping = Boolean(payload?.isTyping)
          setBotTyping(isTyping)
          if (typingTimeout) clearTimeout(typingTimeout)
          if (isTyping) {
            typingTimeout = setTimeout(() => {
              if (isMounted) setBotTyping(false)
            }, 10000)
          }
        })

        socketRef.current = socket
      })
      .catch((err) => {
        console.warn('Could not establish real-time socket, falling back to HTTP API:', err)
        if (isMounted) setSocketStatus('offline')
      })

    return () => {
      isMounted = false
      if (socketRef.current) {
        socketRef.current.disconnect()
        socketRef.current = null
      }
    }
  }, [mode])

  // Inject FAQ preview
  useEffect(() => {
    if (!previewFAQ) return
    const userMsg: ChatMessage = { role: 'user', text: previewFAQ.question, time: getTime() }
    const botMsg: ChatMessage = { role: 'bot', text: previewFAQ.answer, time: getTime() }
    setMessages((prev) => [...prev, userMsg, botMsg])
  }, [previewFAQ])

  // Scroll to bottom
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, botTyping])

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (!file.type.startsWith('image/')) {
      toast.error('Solo se permiten imágenes (JPG, PNG, WebP)')
      return
    }

    if (file.size > 8 * 1024 * 1024) {
      toast.error('La imagen no debe superar los 8MB')
      return
    }

    const reader = new FileReader()
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        setPendingImage({
          dataUrl: reader.result,
          name: file.name,
          file,
        })
      }
    }
    reader.onerror = () => {
      toast.error('No se pudo leer la imagen seleccionada')
    }
    reader.readAsDataURL(file)
    e.target.value = ''
  }

  const handleSendMessage = async () => {
    const text = userInput.trim()
    if (!text && !pendingImage) return

    const imageToSend = pendingImage
    setPendingImage(null)
    setUserInput('')

    const userMsg: ChatMessage = {
      role: 'user',
      text,
      mediaUrl: imageToSend?.dataUrl,
      mediaMimetype: imageToSend?.file.type || 'image/jpeg',
      time: getTime(),
    }
    setMessages((prev) => [...prev, userMsg])
    setBotTyping(true)

    if (mode === 'static') {
      const delay = typingSimulation ? 1200 + Math.random() * 500 : 300
      setTimeout(() => {
        setBotTyping(false)
        setMessages((prev) => [
          ...prev,
          { role: 'bot', text: getBotResponse(text, botName || 'Bot'), time: getTime() },
        ])
      }, delay)
      return
    }

    try {
      const visitorId = visitorIdRef.current
      const result = await sendTestChatMessage({
        text,
        visitorId,
        visitorName: 'Visitante de Prueba',
        mediaUrl: imageToSend?.dataUrl,
        mediaMimetype: imageToSend?.file.type || 'image/jpeg',
      })

      setBotTyping(false)

      const effectiveBotMedia = result.mediaUrl || (result.selectedProduct as any)?.imageUrl
      if (result.reply || effectiveBotMedia) {
        setMessages((prev) => {
          const last = prev[prev.length - 1]
          if (
            last &&
            last.role === 'bot' &&
            last.text === result.reply &&
            last.mediaUrl === effectiveBotMedia
          ) {
            return prev
          }
          return [
            ...prev,
            {
              id: result.conversationId,
              role: 'bot',
              text: result.reply || '',
              mediaUrl: effectiveBotMedia,
              time: getTime(),
              stage: result.stage,
            },
          ]
        })
      }

      if (result.stage) {
        setCurrentStage(result.stage)
      }

      if (result.selectedProduct) {
        setSelectedProduct(result.selectedProduct)
      } else {
        setSelectedProduct(null)
      }
    } catch (err: any) {
      setBotTyping(false)
      toast.error('Error al procesar mensaje con el agente')
      setMessages((prev) => [
        ...prev,
        {
          role: 'bot',
          text: '⚠️ Disculpa, hubo un problema al conectar con el asistente de ventas. Por favor intenta de nuevo.',
          time: getTime(),
        },
      ])
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  const handleResetConversation = async () => {
    if (isResetting) return
    setIsResetting(true)
    try {
      const visitorId = visitorIdRef.current
      if (mode === 'real') {
        await resetTestChatSession(visitorId)
      }
      setMessages(
        welcomeMessage
          ? [{ role: 'bot', text: welcomeMessage, time: getTime() }]
          : []
      )
      setCurrentStage('DISCOVERY')
      setSelectedProduct(null)
      toast.success('Conversación y embudo reiniciados')
    } catch (err) {
      console.error(err)
      toast.error('No se pudo reiniciar la conversación')
    } finally {
      setIsResetting(false)
    }
  }



  const stageBadge = getStageBadge(currentStage)

  const getAvatarContent = (size = 'md') => {
    const sz = size === 'sm' ? 'w-7 h-7 text-xs' : 'w-9 h-9 text-sm'
    const initial = (botName || 'B').charAt(0).toUpperCase()
    if (avatar?.startsWith('#')) {
      return (
        <div
          className={`${sz} rounded-full flex items-center justify-center text-white font-semibold flex-shrink-0`}
          style={{ backgroundColor: avatar }}
        >
          {initial}
        </div>
      )
    }
    if (avatar) {
      return <img src={avatar} alt={botName} className={`${sz} rounded-full object-cover flex-shrink-0`} />
    }
    return (
      <div className={`${sz} rounded-full bg-white border border-emerald-500/30 flex items-center justify-center p-0.5 flex-shrink-0 shadow-xs overflow-hidden`}>
        <img
          src="/new/IridiaChat_Isotipo_Icono_Transparente.png"
          alt={botName || 'IridiaChat'}
          className="w-full h-full object-contain"
        />
      </div>
    )
  }

  return (
    <div className="flex flex-col items-center w-full max-w-[340px]">
      {/* Test Controls Header */}
      <div className="w-full mb-3 bg-white border border-gray-200 rounded-xl p-2.5 shadow-sm space-y-2">
        <div className="flex items-center justify-between gap-1 text-xs">
          {/* Mode Switcher */}
          <div className="flex items-center gap-1 bg-gray-100 p-0.5 rounded-lg">
            <button
              type="button"
              onClick={() => setMode('real')}
              className={`px-2 py-1 rounded-md text-[11px] font-semibold transition-colors flex items-center gap-1 ${
                mode === 'real'
                  ? 'bg-emerald-600 text-white shadow-xs'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Bot className="w-3 h-3" />
              Agente Real IA
            </button>
            <button
              type="button"
              onClick={() => setMode('static')}
              className={`px-2 py-1 rounded-md text-[11px] font-medium transition-colors ${
                mode === 'static'
                  ? 'bg-gray-800 text-white shadow-xs'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Estático
            </button>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={handleResetConversation}
              disabled={isResetting}
              className="p-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors"
              title="Reiniciar conversación"
            >
              <RotateCcw className={`w-3.5 h-3.5 ${isResetting ? 'animate-spin text-emerald-600' : ''}`} />
            </button>
          </div>
        </div>

        {/* Live Funnel Diagnostic Bar */}
        {mode === 'real' && (
          <div className="flex items-center justify-between pt-1 border-t border-gray-100 text-[11px]">
            <div className="flex items-center gap-1.5">
              <span className="text-gray-400">Embudo:</span>
              <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] ${stageBadge.color}`}>
                <span>{stageBadge.icon}</span>
                <span>{stageBadge.label}</span>
              </span>
            </div>

            <div className="flex items-center gap-1.5">
              <span
                className={`inline-block w-1.5 h-1.5 rounded-full ${
                  socketStatus === 'connected'
                    ? 'bg-emerald-500'
                    : socketStatus === 'connecting'
                    ? 'bg-amber-400 animate-pulse'
                    : 'bg-gray-400'
                }`}
              />
              <span className="text-gray-400 text-[10px]">
                {socketStatus === 'connected' ? 'En vivo' : socketStatus === 'connecting' ? 'Conectando' : 'HTTP'}
              </span>
            </div>
          </div>
        )}

        {/* Confirmed Product Chip if in checkout/closing */}
        {selectedProduct && (
          <div className="flex items-center gap-1.5 bg-emerald-50 text-emerald-900 px-2 py-1 rounded text-[11px] border border-emerald-200">
            <CheckCircle2 className="w-3 h-3 text-emerald-600 flex-shrink-0" />
            <span className="truncate font-medium">
              Confirmado: {selectedProduct.name} {selectedProduct.price ? `($${selectedProduct.price})` : ''}
            </span>
          </div>
        )}
      </div>

      {/* WhatsApp Phone Mockup Container */}
      <div
        className="w-full rounded-[2rem] overflow-hidden shadow-2xl bg-gray-900 transition-all"
        style={{ border: '6px solid #1a1a1a' }}
      >
        {/* Status bar */}
        <div className="bg-black px-4 py-1.5 flex items-center justify-between text-white text-[10px]">
          <span className="font-medium">9:41</span>
          <div className="flex items-center gap-1.5">
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M17.778 8.222c-4.296-4.296-11.26-4.296-15.556 0A1 1 0 01.808 6.808c5.076-5.076 13.308-5.076 18.384 0a1 1 0 010 1.414zM14.95 11.05a7 7 0 00-9.9 0 1 1 0 01-1.414-1.414 9 9 0 0112.728 0 1 1 0 01-1.414 1.414zM12.12 13.88a3 3 0 00-4.242 0 1 1 0 01-1.415-1.415 5 5 0 017.072 0 1 1 0 01-1.415 1.415zM9 16a1 1 0 012 0v2a1 1 0 01-2 0v-2z"
                clipRule="evenodd"
              />
            </svg>
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
            </svg>
            <span>●●●</span>
          </div>
        </div>

        {/* WhatsApp header */}
        <div className="bg-[#075E54] px-3 py-2.5 flex items-center gap-2.5">
          <svg className="w-4 h-4 text-white flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
              clipRule="evenodd"
            />
          </svg>
          {getAvatarContent('sm')}
          <div className="flex-1 min-w-0">
            <p className="text-white text-sm font-semibold leading-tight truncate">{botName || 'Mi Bot'}</p>
            <p className="text-white/75 text-[11px] leading-tight">
              {botTyping ? 'escribiendo...' : mode === 'real' ? 'Agente de ventas en línea' : 'en línea'}
            </p>
          </div>
          <div className="flex gap-3.5">
            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
            </svg>
            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
            </svg>
          </div>
        </div>

        {/* Chat area */}
        <div
          className="bg-[#ECE5DD] flex flex-col gap-2 p-3 overflow-y-auto"
          style={{
            height: interactive ? '360px' : '420px',
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23c8c8c8' fill-opacity='0.3'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        >
          {messages.map((msg, i) => {
            const cleanText = (msg.text || '').replace(/\[Imagen:\s*[^\]]+\]/gi, '').trim()
            const hasImage = Boolean(msg.mediaUrl)

            const messageKey = msg.id ?? `preview-msg-${msg.role}-${i}-${msg.time}`

            return msg.role === 'bot' ? (
              <div key={messageKey} className="flex items-end gap-1.5 max-w-[88%]">
                {getAvatarContent('sm')}
                <div className="flex-1 min-w-0">
                  <div className="bg-white rounded-2xl rounded-tl-sm overflow-hidden shadow-sm border border-black/5">
                    {hasImage && (
                      <div
                        className="relative group cursor-pointer overflow-hidden bg-gray-100"
                        onClick={() => setZoomedImage(msg.mediaUrl!)}
                      >
                        <img
                          src={msg.mediaUrl}
                          alt="Producto"
                          className="w-full max-h-52 object-cover transition-transform duration-200 group-hover:scale-105"
                          loading="lazy"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                          <span className="opacity-0 group-hover:opacity-100 transition-opacity bg-black/60 text-white p-1.5 rounded-full shadow">
                            <Maximize2 className="w-3.5 h-3.5" />
                          </span>
                        </div>
                      </div>
                    )}
                    {cleanText ? (
                      <div className="px-3 py-2">
                        <p className="text-[13px] text-gray-800 leading-snug whitespace-pre-wrap break-words">
                          {cleanText}
                        </p>
                      </div>
                    ) : null}
                  </div>
                  <p className="text-[10px] text-gray-400 mt-0.5 ml-1">{msg.time}</p>
                </div>
              </div>
            ) : (
              <div key={messageKey} className="flex justify-end">
                <div className="max-w-[88%]">
                  <div className="bg-[#DCF8C6] rounded-2xl rounded-tr-sm overflow-hidden shadow-sm border border-[#25D366]/20">
                    {hasImage && (
                      <div
                        className="relative group cursor-pointer overflow-hidden bg-black/5"
                        onClick={() => setZoomedImage(msg.mediaUrl!)}
                      >
                        <img
                          src={msg.mediaUrl}
                          alt="Imagen enviada"
                          className="w-full max-h-52 object-cover transition-transform duration-200 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                          <span className="opacity-0 group-hover:opacity-100 transition-opacity bg-black/60 text-white p-1.5 rounded-full shadow">
                            <Maximize2 className="w-3.5 h-3.5" />
                          </span>
                        </div>
                      </div>
                    )}
                    {cleanText ? (
                      <div className="px-3 py-2">
                        <p className="text-[13px] text-gray-800 leading-snug break-words">
                          {cleanText}
                        </p>
                      </div>
                    ) : null}
                  </div>
                  <p className="text-[10px] text-gray-400 mt-0.5 text-right mr-1 flex items-center justify-end gap-1">
                    {msg.time}
                    <svg className="w-3 h-3 text-[#34B7F1]" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </p>
                </div>
              </div>
            )
          })}

          {/* Bot typing indicator */}
          {botTyping && (
            <div className="flex items-end gap-1.5">
              {getAvatarContent('sm')}
              <div className="bg-white rounded-tr-xl rounded-br-xl rounded-tl-sm px-3 py-2.5 shadow-sm">
                <div className="flex gap-1 items-center h-3">
                  <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          )}

          <div ref={chatEndRef} />
        </div>

        {/* Interactive input */}
        {interactive && (
          <div className="bg-[#F0F2F5] border-t border-gray-200">
            {/* Hidden file input */}
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
            />

            {/* Pending image preview bar */}
            {pendingImage && (
              <div className="px-2.5 pt-2 pb-1 flex items-center justify-between bg-[#E9EDEF] border-b border-gray-200">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="relative w-10 h-10 rounded-lg overflow-hidden border border-gray-300 shadow-sm flex-shrink-0 bg-white">
                    <img
                      src={pendingImage.dataUrl}
                      alt="Adjunto"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-gray-800 truncate max-w-[170px]">
                      {pendingImage.name}
                    </p>
                    <p className="text-[10px] text-emerald-600 font-medium">
                      Foto lista para enviar
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setPendingImage(null)}
                  className="p-1 rounded-full text-gray-500 hover:text-red-500 hover:bg-gray-200 transition-colors"
                  title="Quitar foto"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            <div className="px-2 py-2 flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="p-2 rounded-full text-gray-500 hover:text-[#075E54] hover:bg-white transition-colors flex-shrink-0"
                title="Adjuntar imagen de producto"
              >
                <ImageIcon className="w-4 h-4" />
              </button>

              <div className="flex-1 flex items-center bg-white rounded-full px-3 py-1.5 gap-2 shadow-sm min-w-0">
                <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <input
                  type="text"
                  value={userInput}
                  onChange={(e) => setUserInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={
                    pendingImage
                      ? 'Escribe o envía la foto...'
                      : mode === 'real'
                      ? 'Prueba el embudo o adjunta foto...'
                      : 'Escribe un mensaje...'
                  }
                  className="flex-1 text-[13px] outline-none bg-transparent text-gray-800 placeholder:text-gray-400 min-w-0"
                />
              </div>

              <button
                type="button"
                onClick={handleSendMessage}
                disabled={(!userInput.trim() && !pendingImage) || botTyping}
                className="w-9 h-9 rounded-full bg-[#075E54] flex items-center justify-center flex-shrink-0 disabled:opacity-40 hover:bg-[#064E46] transition-colors"
                title="Enviar"
              >
                <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {previewProduct && (
          <div className="bg-white border-t border-gray-200 px-3 py-3">
            <div className="flex items-center gap-3">
              {previewProduct.imageUrl ? (
                <img
                  src={previewProduct.imageUrl}
                  alt={previewProduct.name}
                  className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                />
              ) : (
                <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center text-gray-500 text-sm font-semibold flex-shrink-0">
                  {(previewProduct.name || 'P').charAt(0).toUpperCase()}
                </div>
              )}

              <div className="min-w-0 flex-1">
                <p className="text-[10px] uppercase tracking-wide text-gray-500">
                  Producto sugerido
                </p>
                <p className="text-sm font-semibold text-gray-900 truncate">
                  {previewProduct.name}
                </p>
                {previewProduct.description && (
                  <p className="text-xs text-gray-600 truncate" title={previewProduct.description}>
                    {previewProduct.description}
                  </p>
                )}
                <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-gray-500">
                  <span className="rounded-full bg-gray-100 px-2 py-0.5 font-medium text-gray-700">
                    {new Intl.NumberFormat('es-ES', {
                      style: 'currency',
                      currency: previewProduct.currency || 'USD',
                    }).format((previewProduct.priceCents || 0) / 100)}
                  </span>
                  <span>Stock: {previewProduct.stock}</span>
                  {previewProduct.category && <span>{previewProduct.category}</span>}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Bottom bar */}
        <div className="bg-black h-1.5" />
      </div>



      {/* Image Lightbox Modal */}
      {zoomedImage && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 backdrop-blur-sm"
          onClick={() => setZoomedImage(null)}
        >
          <div
            className="relative max-w-xl max-h-[85vh] bg-transparent rounded-2xl overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setZoomedImage(null)}
              className="absolute top-3 right-3 bg-black/60 hover:bg-black/90 text-white p-1.5 rounded-full shadow transition-all z-10"
              title="Cerrar imagen"
            >
              <X className="w-5 h-5" />
            </button>
            <img
              src={zoomedImage}
              alt="Vista ampliada"
              className="w-full h-auto max-h-[80vh] object-contain rounded-xl shadow-lg bg-black/30"
            />
          </div>
        </div>
      )}
    </div>
  )
}
